import React, { useMemo, useState } from "react";
import { Zap, Minus, Plus } from "lucide-react";
import { toast } from "sonner";

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { api, formatApiError } from "../lib/api";

// Iter71 Fix 4 — Split the single "Minutes (1-240)" input into separate
// Hours + Minutes inputs so HR can compensate a full-shift (8h+) no-show
// without stitching multiple 240m entries. Total is clamped to 24h so a
// typo can't produce an obviously wrong week's-worth of adjustment.
const MAX_HOURS = 23;
const MAX_MINUTES_IN_HOUR = 59;
const MAX_TOTAL_MINUTES = 1440;      // 24h — matches backend ADJUSTMENT_MAX
const DEFAULT_HOURS = 0;
const DEFAULT_MINUTES = 30;

const _clampInt = (raw, min, max) => {
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n)) return min;
    return Math.max(min, Math.min(max, n));
};

const _fmtHM = (mins) => {
    const abs = Math.abs(mins);
    const h = Math.floor(abs / 60);
    const m = abs % 60;
    if (h === 0) return `${m}m`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
};

/**
 * Iter60.p21 — AdjustHoursDialog
 * Iter71 Fix 4 — Hours + Minutes inputs (was: Minutes-only capped at 240).
 *
 * Add ± hour adjustments to one Construction row (single mode) or to
 * a batch of them (bulk mode). Preconditions/RBAC are enforced on the
 * server; the client only shows this dialog when the current selection is
 * eligible (see the "Adjust Hours" button/kebab item on Attendance.jsx).
 *
 * Props:
 *   - rowIds: array of daily_id strings to apply the adjustment to
 *   - onClose: () => void — parent-controlled close handler
 *   - onDone:  () => void — fires after the API call resolves successfully;
 *                           parent uses this to refresh the table
 */
export default function AdjustHoursDialog({ rowIds, onClose, onDone }) {
    const isBulk = (rowIds?.length || 0) > 1;
    const [sign, setSign] = useState("add"); // "add" | "deduct"
    const [hours, setHours] = useState(String(DEFAULT_HOURS));
    const [minutes, setMinutes] = useState(String(DEFAULT_MINUTES));
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);

    const hoursInt = parseInt(hours, 10);
    const minsInt = parseInt(minutes, 10);
    const hoursValid = Number.isFinite(hoursInt) && hoursInt >= 0 && hoursInt <= MAX_HOURS;
    const minsValid = Number.isFinite(minsInt) && minsInt >= 0 && minsInt <= MAX_MINUTES_IN_HOUR;
    const totalMinutes = (hoursValid ? hoursInt : 0) * 60 + (minsValid ? minsInt : 0);
    const totalValid = totalMinutes > 0 && totalMinutes <= MAX_TOTAL_MINUTES;
    const noteValid = note.trim().length >= 5;
    const canSubmit = hoursValid && minsValid && totalValid && noteValid && !busy && (rowIds?.length || 0) > 0;

    const signedMinutes = useMemo(() => {
        if (!totalValid) return 0;
        return sign === "deduct" ? -totalMinutes : totalMinutes;
    }, [totalValid, totalMinutes, sign]);

    const apply = async () => {
        setBusy(true);
        try {
            if (isBulk) {
                const { data } = await api.post(
                    "/attendance/daily/adjustments/bulk",
                    { row_ids: rowIds, minutes: signedMinutes, note: note.trim() },
                );
                const c = data.counts || {};
                if (c.applied > 0) {
                    toast.success(
                        `Adjusted ${c.applied} row${c.applied === 1 ? "" : "s"} by ${signedMinutes > 0 ? "+" : "−"}${_fmtHM(signedMinutes)}.`,
                    );
                }
                if (c.skipped > 0) {
                    toast.info(
                        `${c.skipped} row${c.skipped === 1 ? "" : "s"} skipped (not Construction, or RBAC).`,
                    );
                }
                if (c.errors > 0) {
                    toast.error(`${c.errors} row${c.errors === 1 ? "" : "s"} failed.`);
                    // eslint-disable-next-line no-console
                    console.warn("bulk-adjust errors:", data.errors);
                }
            } else {
                await api.post(
                    `/attendance/daily/${rowIds[0]}/adjustments`,
                    { minutes: signedMinutes, note: note.trim() },
                );
                toast.success(
                    `Adjustment ${signedMinutes > 0 ? "+" : "−"}${_fmtHM(signedMinutes)} applied.`,
                );
            }
            onDone();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const isAdd = sign === "add";
    const _bumpHours = (delta) => setHours(String(_clampInt((hoursInt || 0) + delta, 0, MAX_HOURS)));
    const _bumpMinutes = (delta) => setMinutes(String(_clampInt((minsInt || 0) + delta, 0, MAX_MINUTES_IN_HOUR)));

    // Sign for the preview: honour the toggle regardless of validity so
    // the "will apply" copy always matches the button's visual state.
    const previewSign = isAdd ? "+" : "−";

    return (
        <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
            <DialogContent data-testid="att-adjust-hours-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 asl-navy-text">
                        <Zap className="h-5 w-5 text-[color:var(--asl-crimson)]" />
                        {isBulk ? `Adjust hours for ${rowIds.length} rows` : "Adjust hours"}
                    </DialogTitle>
                    <DialogDescription>
                        Add or deduct time from
                        {isBulk ? ` these ${rowIds.length} Construction rows` : " this Construction row"}.
                        The adjustment flows through to payroll and is recorded in the audit log with a mandatory note.
                    </DialogDescription>
                </DialogHeader>

                {/* Sign toggle */}
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => setSign("add")}
                        className={`flex-1 rounded-md px-3 py-2 text-sm font-medium border transition-colors ${
                            isAdd
                                ? "bg-emerald-600 text-white border-emerald-600"
                                : "bg-white text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                        }`}
                        data-testid="att-adjust-sign-add"
                    >
                        <Plus className="inline h-3.5 w-3.5 mr-1" /> Add hours
                    </button>
                    <button
                        type="button"
                        onClick={() => setSign("deduct")}
                        className={`flex-1 rounded-md px-3 py-2 text-sm font-medium border transition-colors ${
                            !isAdd
                                ? "bg-[color:var(--asl-crimson)] text-white border-[color:var(--asl-crimson)]"
                                : "bg-white text-[color:var(--asl-crimson)] border-rose-200 hover:bg-rose-50"
                        }`}
                        data-testid="att-adjust-sign-deduct"
                    >
                        <Minus className="inline h-3.5 w-3.5 mr-1" /> Deduct hours
                    </button>
                </div>

                {/* Iter71 Fix 4 — Hours + Minutes stepper pair */}
                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                        <Label htmlFor="att-adjust-hours" className="text-xs">
                            Hours <span className="text-rose-600">*</span>{" "}
                            <span className="text-[10px] text-[color:var(--asl-muted)]">(0–{MAX_HOURS})</span>
                        </Label>
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                className="w-9 h-9 rounded-md border border-[color:var(--asl-border)] hover:bg-gray-50"
                                onClick={() => _bumpHours(-1)}
                                aria-label="Decrease hours by 1"
                                data-testid="att-adjust-hours-minus"
                            >
                                −
                            </button>
                            <Input
                                id="att-adjust-hours"
                                type="number"
                                min={0}
                                max={MAX_HOURS}
                                value={hours}
                                onChange={(e) => setHours(e.target.value)}
                                className="text-center mono"
                                data-testid="att-adjust-hours"
                            />
                            <button
                                type="button"
                                className="w-9 h-9 rounded-md border border-[color:var(--asl-border)] hover:bg-gray-50"
                                onClick={() => _bumpHours(1)}
                                aria-label="Increase hours by 1"
                                data-testid="att-adjust-hours-plus"
                            >
                                +
                            </button>
                        </div>
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="att-adjust-minutes" className="text-xs">
                            Minutes <span className="text-rose-600">*</span>{" "}
                            <span className="text-[10px] text-[color:var(--asl-muted)]">(0–{MAX_MINUTES_IN_HOUR})</span>
                        </Label>
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                className="w-9 h-9 rounded-md border border-[color:var(--asl-border)] hover:bg-gray-50"
                                onClick={() => _bumpMinutes(-5)}
                                aria-label="Decrease minutes by 5"
                                data-testid="att-adjust-minutes-minus"
                            >
                                −
                            </button>
                            <Input
                                id="att-adjust-minutes"
                                type="number"
                                min={0}
                                max={MAX_MINUTES_IN_HOUR}
                                value={minutes}
                                onChange={(e) => setMinutes(e.target.value)}
                                className="text-center mono"
                                data-testid="att-adjust-minutes"
                            />
                            <button
                                type="button"
                                className="w-9 h-9 rounded-md border border-[color:var(--asl-border)] hover:bg-gray-50"
                                onClick={() => _bumpMinutes(5)}
                                aria-label="Increase minutes by 5"
                                data-testid="att-adjust-minutes-plus"
                            >
                                +
                            </button>
                        </div>
                    </div>
                </div>
                <div
                    className="text-[11px] text-[color:var(--asl-muted)]"
                    data-testid="att-adjust-total-hint"
                >
                    Total: <span className="font-semibold asl-navy-text">{_fmtHM(totalMinutes)}</span>{" "}
                    <span className="mono">= {totalMinutes}m</span>
                </div>
                {!totalValid && (
                    <div className="text-[11px] text-rose-600" data-testid="att-adjust-error-total">
                        {totalMinutes === 0
                            ? "Enter at least 1 minute (Hours or Minutes)."
                            : `Total cannot exceed ${_fmtHM(MAX_TOTAL_MINUTES)}.`}
                    </div>
                )}

                {/* Note */}
                <div className="space-y-1">
                    <Label htmlFor="att-adjust-note" className="text-xs">
                        Note <span className="text-rose-600">*</span>{" "}
                        <span className="text-[10px] text-[color:var(--asl-muted)]">
                            (min 5 chars — recorded in the audit log)
                        </span>
                    </Label>
                    <textarea
                        id="att-adjust-note"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        rows={3}
                        placeholder='e.g. "Worked through lunch" · "IC food delivery, paid by camp"'
                        className="w-full rounded-md border border-[color:var(--asl-border)] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--asl-navy)]"
                        data-testid="att-adjust-note"
                    />
                </div>

                {/* Live preview — Iter71 Fix 4 uses Xh Ym format */}
                {totalValid && (
                    <div
                        className={`text-sm rounded px-3 py-2 border ${
                            isAdd
                                ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                                : "bg-rose-50 border-rose-200 text-[color:var(--asl-crimson)]"
                        }`}
                        data-testid="att-adjust-preview"
                    >
                        This will apply{" "}
                        <span className="font-semibold">{previewSign}{_fmtHM(totalMinutes)}</span>{" "}
                        to {rowIds.length} row{rowIds.length === 1 ? "" : "s"}. Payable minutes will change by{" "}
                        <span className="font-semibold">{previewSign}{totalMinutes}m</span> each.
                    </div>
                )}

                <DialogFooter>
                    <Button variant="ghost" onClick={onClose} disabled={busy} data-testid="att-adjust-cancel">
                        Cancel
                    </Button>
                    <Button
                        className={
                            isAdd
                                ? "bg-emerald-600 text-white hover:bg-emerald-700"
                                : "bg-[color:var(--asl-crimson)] text-white hover:brightness-95"
                        }
                        onClick={apply}
                        disabled={!canSubmit}
                        data-testid="att-adjust-apply"
                    >
                        {busy ? "Applying…" : isBulk ? `Apply to ${rowIds.length}` : "Apply"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
